import $ from 'jquery';
import layout from '../Layout';
import VariantHelper from '../VariantHelper';
import Quantity from './Quantity';
import QuantitySubmit from './QuantitySubmit';
import Cart from '../components/cart/Cart';
import Popover from '../helpers/Popover';

import { selectors, classes, events } from '../Constants';
import ScrollHinter from '../helpers/ScrollHinter';

export default class ProductAtc {
  constructor(options = {}) {
    this.$window = $(window);

    const defaults = {
      el: null,
      container: null,
      moneyFormat: null,
      product: null,
      cartRedirect: null,
      variantAvailable: null,
      textStrings: {
        add_to_cart: '',
        quantity_text: '',
        quantity_disable: '',
      },
      onATCSubmit: () => {},
      onATCSuccess: () => {},
    };

    this.config = $.extend(true, {}, defaults, options);

    if (
      !this.config.el
      || !this.config.product
      || !this.config.textStrings
    ) {
      return console.warn('Unable to initiate ProductAtc.js');
    }

    this.$el = $(this.config.el);
    this.cartEl = this.config.el.querySelector(selectors.cart.el);
    this.$cartEl = $(this.cartEl);
    this.form = this.config.el.querySelector(selectors.product.form.el);
    this.quantityEl = this.config.el.querySelector(selectors.quantity.input);
    this.trigger = this.config.el.querySelector(selectors.popover.triggers.atcSteps);
    this.triggers = this.config.el.querySelectorAll(selectors.popover.triggers.atcToggle);
    this.contentForm = this.config.el.querySelector(selectors.popover.contents.atcStepForm);
    this.contentCart = this.config.el.querySelector(selectors.popover.contents.atcStepCart);
    this.dcbSubmit = this.config.el.querySelector(selectors.product.form.dcbSubmit);
    this.$dcbSubmit = $(this.dcbSubmit);
    this.$dcbSubmitText = this.$dcbSubmit.find(selectors.product.form.dcbSubmitText);

    this.optionGroups = this.form.querySelectorAll('.option-value-group');
    this.scrollHinters = [];

    this.triggerFormPopover = new Popover(
      this.trigger,
      this.contentForm,
    );
    this.triggerCartPopover = new Popover(
      this.trigger,
      this.contentCart,
    );
    this.formCartPopoverFixed = new Popover(
      this.contentForm,
      this.contentCart,
    );
    this.formCartPopoverAbsolute = new Popover(
      this.contentForm,
      this.contentCart,
      {
        fixed: false,
        boundaryElement: this.config.container,
      },
    );
    this.activePopover = this.formCartPopoverAbsolute;

    this.formAtc = this.config.el.querySelector(selectors.product.form.atc);
    this.isStatic = this.$el.hasClass(classes.product.atc.static);
    this.isDefaultVariant = this.$el.hasClass(classes.product.atc.defaultVariant);
    this.submitting = false;

    this.variantHelper = null;
    this.quantity = null;
    this.cart = null;

    this.onTriggerClick = this._onTriggerClick.bind(this);
    this.onBodyClick = this._onBodyClick.bind(this);
    this.onSubmit = this._onSubmit.bind(this);
    this.onCartReloadFinish = this._onCartReloadFinish.bind(this);
    this.onCartItemRemoveFinish = this._onCartItemRemoveFinish.bind(this);
    this.onVariantSwitch = this._onVariantSwitch.bind(this);
    this.updatePopper = this._updatePopper.bind(this);

    this._init();
  }

  unload() {
    if (this.variantHelper) {
      this.variantHelper.unload();
      this.variantHelper = null;
    }

    this.quantity.unload();
    this.quantity = null;

    this.cart.cartRequests.restoreHandlers();

    if (this.triggers.length > 0) {
      for (let i = 0; i < this.triggers.length; i++) {
        this.triggers[i].removeEventListener('click', this.onTriggerClick);
      }
    }

    this.scrollHinters.forEach((hinter) => {
      hinter.unload();
    });

    if (!this.isStatic) {
      layout.offBreakpointChange(this._onBreakpointChange);
    }

    document.body.removeEventListener('click', this.onBodyClick);
    this.form.removeEventListener('submit', this.onSubmit);
    this.$window.off('resize', this.updatePopper);
    this.$window.off(events.cart.inlineVisible, this.updatePopper);
    this.$cartEl.off(events.cart.reloadFinish, this.onCartReloadFinish);
    this.$window.off(events.cart.item.removingFinished, this.onCartItemRemoveFinish);
    this.$window.off('product-variant-switch', this.onVariantSwitch);
  }

  _init() {
    const $productVariants = $(selectors.product.form.variants, this.form);
    const $productOptions = $(selectors.product.form.productOption, this.form);

    this.cart = new Cart(this.cartEl, {
      moneyFormat: this.config.moneyFormat,
    });

    this.variantHelper = $productOptions.length
      ? new VariantHelper(
        this.config.product,
        $productVariants,
        $productOptions,
      )
      : null;

    if (this.dcbSubmit) {
      this.quantity = new Quantity(
        this.form.querySelector(selectors.product.form.quantity),
      );
    } else {
      this.quantity = new QuantitySubmit(
        this.form.querySelector(selectors.product.form.quantity),
        {
          submitCallback: this.onSubmit,
          text: this.config.textStrings.quantity_text,
          disableText: this.config.textStrings.quantity_disable,
        },
      );
    }

    this._atcState(this.config.variantAvailable);

    if (this.triggers.length > 0) {
      for (let i = 0; i < this.triggers.length; i++) {
        this.triggers[i].addEventListener('click', this.onTriggerClick);
      }
    }

    if (!this.isStatic) {
      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      layout.onBreakpointChange(this._onBreakpointChange);

      this._enablePopoverByBreakpoint(layout.getBreakpoint());
      this._addScrollHinters();
    }

    document.body.addEventListener('click', this.onBodyClick);
    this.form.addEventListener('submit', this.onSubmit);
    this.$window.on('resize', this.updatePopper);
    this.$window.on(events.cart.inlineVisible, this.updatePopper);
    this.$cartEl.on(events.cart.reloadFinish, this.onCartReloadFinish);
    this.$window.on(events.cart.item.removingFinished, this.onCartItemRemoveFinish);
    this.$window.on('product-variant-switch', this.onVariantSwitch);
  }

  _addScrollHinters() {
    for (let i = 0; i < this.optionGroups.length; i += 1) {
      this.scrollHinters.push(new ScrollHinter(this.optionGroups[i]));
    }
  }

  _checkScrollHinters() {
    this.scrollHinters.forEach((hinter) => {
      hinter.checkScroll();
    });
  }

  _onBreakpointChange(events, breakpoints = {}) {
    this._enablePopoverByBreakpoint(breakpoints.current);
  }

  _changePopover(popover, force = false) {
    const deferred = $.Deferred();

    if (this.activePopover === popover) {
      return deferred.resolve().promise();
    }

    this.activePopover.disable(force).then(() => {
      this.activePopover = popover;
      this.activePopover.enable();

      deferred.resolve();
    });

    return deferred.promise();
  }

  _enablePopoverByBreakpoint(breakpoint) {
    let popover = null;

    switch (breakpoint) {
      case 'XS':
      case 'S':
        popover = this.isDefaultVariant
          ? this.formCartPopoverFixed
          : this.triggerFormPopover;
        break;
      case 'M':
      case 'L':
      case 'XL':
      default:
        popover = this.formCartPopoverAbsolute;
        break;
    }

    this._changePopover(popover, true);
  }

  _onTriggerClick(event) {
    event.stopPropagation();

    if (this.activePopover === this.triggerCartPopover) {
      this._changePopover(this.triggerFormPopover);
    } else {
      this.activePopover.toggle().then(() => {
        if (this.activePopover.attachment === this.contentForm) {
          this._checkScrollHinters();
        }
      });
    }
  }

  _onBodyClick() {
    this.activePopover.close();
  }

  _onSubmit(event) {
    if (this.config.cartRedirect) {
      return;
    }

    event.preventDefault();

    if (this.quantityEl.getAttribute('disabled')) {
      return;
    }

    if (this.config.onATCSubmit) {
      this.config.onATCSubmit();
    }

    const form = $(this.form).serializeArray();

    let variant = null;
    let quantity = null;

    form.forEach((data) => {
      switch (data.name) {
        case 'id':
          variant = data.value;
          break;
        case 'quantity':
          quantity = data.value;
          break;
        default:
          break;
      }
    });

    if (!variant || !quantity) {
      // TODO: Better error messaging
      console.error('Error submitting product form.');
      return;
    }

    this.formAtc.classList.add(classes.product.atc.loading);
    this.cart.cartRequests.submitItemRequest(variant, quantity, true);

    this.submitting = true;
  }

  _updatePopper() {
    this.activePopover.update();
  }

  _onCartReloadFinish(event, data) {
    if (!this.submitting) {
      return;
    }

    const error = data.error;

    this.formAtc.classList.remove(classes.product.atc.loading);

    if (error) {
      return;
    }

    if (this.triggers.length > 0) {
      for (let i = 0; i < this.triggers.length; i++) {
        this.triggers[i].removeEventListener('click', this.onTriggerClick);
      }

      this.triggers = this.config.el.querySelectorAll(selectors.popover.triggers.atcToggle);

      for (let i = 0; i < this.triggers.length; i++) {
        this.triggers[i].addEventListener('click', this.onTriggerClick);
      }
    }

    if (this.config.onATCSuccess) {
      this.config.onATCSuccess();
    }

    if (this.activePopover === this.triggerFormPopover) {
      this._changePopover(this.triggerCartPopover).then(
        () => this.activePopover.open(),
      );
    } else {
      this.activePopover.open();
    }

    this.submitting = false;
  }

  _onCartItemRemoveFinish(event, data) {
    const cart = data.cart;

    if (this.cart !== cart) {
      return;
    }

    this.activePopover.update();
  }

  _onVariantSwitch(event, data) {
    const variant = data.variant;

    this._atcState(variant.available);
  }

  _atcState(available) {
    this.quantity.setDisable(!available);

    if (this.$dcbSubmit.length) {
      this.$dcbSubmit.prop('disabled', !available);
      this.$dcbSubmitText.text(available
        ? this.config.textStrings.add_to_cart
        : this.config.textStrings.quantity_disable);
    }
  }
}
